import type { Locale, PageKey, ServiceKey } from './content/types'
import { SERVICE_KEYS, SERVICE_SLUGS } from './data/services'
import { LEGAL_PATHS } from '../../legal/paths'

/**
 * Verde Valsesia 2 — the route table.
 *
 * This file is the ONLY source of truth for public paths. The React
 * router, the per-route <head> (head.ts), the prerender entry list, the
 * sitemap and the language switcher all read `ROUTES`; nothing else may
 * hard-code a URL. Italian lives at the root, English under `/en/` with
 * translated slugs (`data/services.ts` owns the service segments).
 *
 * Every path is slash-terminated, because the prerender writes
 * `<path>/index.html` and nginx serves the directory form — a canonical
 * without the trailing slash would 301 and split the ranking signal.
 *
 * Pure module: no DOM, no React. Imported from Node (prerender, sitemap).
 */

export const SITE_ORIGIN = 'https://verdevalsesia.it'

export type RouteKind = 'home' | 'service' | 'zone' | 'contact' | 'legal' | 'credits'

export interface RouteDef {
  key: Exclude<PageKey, 'notFound'>
  kind: RouteKind
  paths: Record<Locale, string>
  serviceKey?: ServiceKey
  /**
   * A page this site links to and lists (sitemap, hreflang, llms) but does not
   * render: the shared legal section (src/legal/) writes it as a static
   * document. The SPA router and the prerender skip it; link to it with a
   * plain <a>, never a router <Link>.
   */
  static?: true
}

const it = (s: string) => (s ? `/${s}/` : '/')
const en = (s: string) => (s ? `/en/${s}/` : '/en/')

/** Spec §4.1 order: home, the seven services, zone, contatti, the three legal pages, crediti. */
export const ROUTES: readonly RouteDef[] = [
  { key: 'home', kind: 'home', paths: { it: it(''), en: en('') } },
  ...SERVICE_KEYS.map(
    (k): RouteDef => ({
      key: k,
      kind: 'service',
      serviceKey: k,
      paths: { it: it(SERVICE_SLUGS[k].it), en: en(SERVICE_SLUGS[k].en) },
    }),
  ),
  { key: 'zone', kind: 'zone', paths: { it: it('zone'), en: en('areas') } },
  { key: 'contatti', kind: 'contact', paths: { it: it('contatti'), en: en('contact') } },
  // The shared legal section: its URLs, its pages (src/legal/).
  { key: 'privacy', kind: 'legal', static: true, paths: LEGAL_PATHS.privacy },
  { key: 'cookie', kind: 'legal', static: true, paths: LEGAL_PATHS.cookie },
  { key: 'dataDeletion', kind: 'legal', static: true, paths: LEGAL_PATHS.dataDeletion },
  { key: 'crediti', kind: 'credits', static: true, paths: LEGAL_PATHS.crediti },
]

/** Throws on an unknown key: a typo in a link belongs in the build, not in a 404. */
export function routeFor(key: RouteDef['key']): RouteDef {
  const r = ROUTES.find((x) => x.key === key)
  if (!r) throw new Error(`unknown route ${key}`)
  return r
}

export function pathFor(key: RouteDef['key'], locale: Locale): string {
  return routeFor(key).paths[locale]
}

/** Leading + trailing slash, duplicate slashes collapsed. `'en/areas'` → `'/en/areas/'`. */
export function normalizePath(pathname: string): string {
  let p = '/' + pathname.replace(/^\/+/, '').replace(/\/{2,}/g, '/')
  if (!p.endsWith('/')) p += '/'
  return p
}

/** `/en` and anything under `/en/` is English; everything else Italian. */
export function localeFromPath(pathname: string): Locale {
  const p = normalizePath(pathname)
  return p === '/en/' || p.startsWith('/en/') ? 'en' : 'it'
}

export function matchRoute(pathname: string): { route: RouteDef; locale: Locale } | null {
  const p = normalizePath(pathname)
  for (const route of ROUTES) {
    if (route.paths.it === p) return { route, locale: 'it' }
    if (route.paths.en === p) return { route, locale: 'en' }
  }
  return null
}

/** The sibling page in the other locale; the target home when the path is off-table. */
export function altPath(pathname: string, target: Locale): string {
  const m = matchRoute(pathname)
  return m ? m.route.paths[target] : pathFor('home', target)
}

export function absoluteUrl(path: string): string {
  return SITE_ORIGIN + normalizePath(path)
}

/** Public root of this site's own media (photos, OG cards). Files, so never `normalizePath`d. */
export const MEDIA_BASE = '/media/verde-valsesia-2'

/**
 * OG card for one route × locale, e.g. `/media/verde-valsesia-2/og/home-it.jpg`.
 * It lives here rather than in head.ts because jsonld.ts needs the home card
 * for the business `image`, and head.ts already imports jsonld.ts — putting it
 * there would make the two modules circular. head.ts re-exports it.
 */
export function ogImagePath(key: RouteDef['key'], locale: Locale): string {
  return `${MEDIA_BASE}/og/${key}-${locale}.jpg`
}

/** Absolute URL of a file under {@link MEDIA_BASE} (no trailing slash added). */
export function mediaUrl(file: string): string {
  return `${SITE_ORIGIN}${MEDIA_BASE}/${file.replace(/^\/+/, '')}`
}

/** One prerendered page; consumed by entry-server.tsx and the seo/* generators. */
export interface PrerenderEntry {
  path: string
  key: RouteDef['key']
  locale: Locale
}
